'use client'

import { useBreakpoints } from '@/lib/device/useBreakpoint'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

export const DEFINITION_LIST_SYNC_MAX_ROWS = 6

type DefinitionListSyncContext = {
  rowSizes: number[]
  reportRowSizes: (sizes: number[]) => void
  isFullDisplay: boolean
  toggleFullDisplay: () => void
}

const DefinitionListSyncContext = createContext<DefinitionListSyncContext | null>(null)

export function DefinitionListProvider({ children }: { children: ReactNode }) {
  const [rowSizes, setRowSizes] = useState<number[]>([])
  const [isFullDisplay, setIsFullDisplay] = useState(false)
  const { isDesktopWidth } = useBreakpoints()

  const reportRowSizes = useCallback((sizes: number[]) => {
    setRowSizes((previousSizes) => {
      const nextSizes = sizes.map((size, index) => Math.max(size, previousSizes[index] ?? 0))

      if (
        nextSizes.length === previousSizes.length &&
        nextSizes.every((size, index) => size === previousSizes[index])
      ) {
        return previousSizes
      }

      return nextSizes
    })
  }, [])

  const toggleFullDisplay = useCallback(() => {
    setIsFullDisplay((previous) => !previous)
  }, [])

  const value = useMemo(
    () => ({
      rowSizes,
      reportRowSizes,
      isFullDisplay,
      toggleFullDisplay,
    }),
    [rowSizes, reportRowSizes, isFullDisplay, toggleFullDisplay],
  )

  useEffect(() => {
    setRowSizes([])
  }, [isDesktopWidth])

  return (
    <DefinitionListSyncContext.Provider value={value}>
      {children}
    </DefinitionListSyncContext.Provider>
  )
}

export function useDefinitionListSync() {
  return useContext(DefinitionListSyncContext)
}

export type DefinitionListSyncContextValue = NonNullable<ReturnType<typeof useDefinitionListSync>>
